import type { ReactNode } from 'react';
import { cx } from '../primitives/cx.js';
import { passthrough } from '../primitives/passthrough.js';
import { Icon } from '../primitives/Icon.tsx';
import { Num } from '../primitives/Num.tsx';
import { SectionLabel } from '../primitives/Surface.tsx';

/**
 * MetricTile - a single KPI, with what it means beside it.
 *
 * The value arrives already formatted: this component never sees a number. A
 * tile that formatted its own figure would be a second answer to a question
 * the formatter has already answered, and the two would disagree about
 * currency first (P-1, D-018).
 *
 * `context` is not decoration. A money figure alone cannot distinguish "no
 * deals" from "deals worth nothing", and a tile showing a zero with nothing
 * beside it says neither.
 */

export interface MetricTileLabels {
  /** Announced with the trend, e.g. "up 12% on last month". */
  up: (change: string) => string;
  down: (change: string) => string;
  flat: string;
}

export interface MetricTileProps {
  label: string;
  /** Already through the formatter. Never a raw number. */
  value: string;
  /** The sentence that says what kind of value this is. */
  context?: ReactNode;
  trend?: { direction: 'up' | 'down' | 'flat'; change: string; good: boolean } | undefined;
  labels: MetricTileLabels;
  href?: string | undefined;
  align?: 'start' | 'end';
}

export function MetricTile({
  label,
  value,
  context,
  trend,
  labels,
  href,
  align = 'end',
  ...rest
}: MetricTileProps & Record<string, unknown>): JSX.Element {
  const body = (
    <div
      {...passthrough(rest)}
      data-component="MetricTile"
      className={cx('flex flex-col gap-0.5', align === 'end' ? 'items-end text-end' : 'items-start')}
    >
      <SectionLabel>{label}</SectionLabel>
      {/* Serif at h3, which is what 2a sets a strip figure at. It was h1 -
          40px under the new scale - and four of those in a row shout down the
          plate figure they are meant to sit beneath. */}
      <Num align={align} className="font-serif text-h3 text-ink">
        {value}
      </Num>
      {context ? <span className="text-meta text-ink-meta">{context}</span> : null}
      {trend ? (
        <span
          data-trend={trend.direction}
          className={cx(
            'inline-flex items-center gap-1 text-meta',
            trend.direction === 'flat'
              ? 'text-ink-meta'
              : trend.good
                ? 'text-success-text'
                : 'text-danger-text',
          )}
        >
          <Icon
            name={
              trend.direction === 'up'
                ? 'TrendUp'
                : trend.direction === 'down'
                  ? 'TrendDown'
                  : 'Minus'
            }
            size="inline"
            decorative
          />
          <span>
            {trend.direction === 'up'
              ? labels.up(trend.change)
              : trend.direction === 'down'
                ? labels.down(trend.change)
                : labels.flat}
          </span>
        </span>
      ) : null}
    </div>
  );

  return href ? (
    <a
      href={href}
      className="rounded-card focus-visible:outline-none"
    >
      {body}
    </a>
  ) : (
    body
  );
}
